import { useCallback, useEffect, useRef } from 'react';
import { buildLeadPayload, isLeadEmailValid } from '@/lib/applyLead';

/** True when this browser honours fetch's keepalive flag, which is what
 *  lets a request outlive the page that started it. */
const KEEPALIVE_SUPPORTED = (() => {
  try {
    return 'keepalive' in new Request('/');
  } catch (e) {
    return false;
  }
})();

/**
 * Capture the unfinished application, at every step boundary and once more
 * when the visitor leaves.
 *
 * The boundary calls alone would miss anyone who abandons *inside* a step,
 * and step 1 is the only step every visitor sees. The hide handler is what
 * lifts capture from "deepest completed step" to "deepest field touched",
 * and it costs no friction because it fires on the way out.
 *
 * `visibilitychange` and `pagehide` are both subscribed on purpose:
 * coverage differs across browsers, and `beforeunload` does not fire
 * reliably on mobile — which is most of this audience.
 */
export default function useApplyLead(api, form, step, submittedRef) {
  // The latest form and step live in a ref so `sendLead` keeps a stable
  // identity. Putting `form` in the dependency array instead would tear
  // down and re-subscribe both listeners on every keystroke.
  const latest = useRef({ form, step });
  latest.current = { form, step };

  // Tab-switching fires visibilitychange repeatedly. Duplicate writes are
  // harmless under the server's merge semantics, but there is no reason to
  // post an unchanged body over and over.
  const lastSent = useRef('');

  /** Fire the capture. Resolves to the server's answer about this email —
   *  `{ account_exists }` — or null when nothing was sent. The step-boundary
   *  caller awaits it; the page-hide caller ignores it. */
  const sendLead = useCallback((opts = {}) => {
    // A submitted application is not an abandon.
    if (submittedRef.current) return Promise.resolve(null);

    const { form: current, step: currentStep } = latest.current;
    if (!isLeadEmailValid(current.email)) return Promise.resolve(null);

    const body = JSON.stringify(buildLeadPayload(current, currentStep));
    // The de-dupe guard is skipped when the caller wants the answer back:
    // stepping forward and back must not leave the wizard without one.
    if (!opts.wantAnswer && body === lastSent.current) return Promise.resolve(null);
    lastSent.current = body;

    const url = `${api}/apply/lead`;

    // sendBeacon cannot set headers, so a JSON blob would be a non-simple
    // content type needing a CORS preflight it cannot complete during
    // unload. Keepalive fetch takes normal headers and reuses the exact
    // contract the boundary write already used — which also means the
    // preflight is already cached by the time this fires.
    if (!KEEPALIVE_SUPPORTED && navigator.sendBeacon) {
      try {
        navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }));
      } catch (e) {
        // Nothing to do. Capture is best-effort by design.
      }
      return Promise.resolve(null);
    }

    try {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      })
        .then(r => (r.ok ? r.json() : null))
        // Never surfaced to the applicant, never blocks the wizard. A null
        // answer just means the account check is skipped and they see the
        // ordinary create-account form, which still works for everyone.
        .catch(() => null);
    } catch (e) {
      return Promise.resolve(null);
    }
  }, [api, submittedRef]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendLead();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', sendLead);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', sendLead);
    };
  }, [sendLead]);

  return sendLead;
}
